import {
  FilterByPoisParamsRequestData,
  PoisFilter,
} from '../plan/filter/pois.type'
import layerService from '@/layers/layerService'
import {
  POI_LAYER,
  CUSTOM_POI_LAYER,
  ISOCHRONES_LAYER,
  POI_LOGO_LAYER,
  CUSTOM_POI_LOGO_LAYER,
} from '@/layers'
import {
  updatePoiLogosCache,
  isPoiLogosCacheEmpty,
  clearPoiLogosCache,
} from '@/layers/poi-logos/poi-logos-cache'
import { FetchLayerDataResult } from '@/services/fetch-layer/fetch-layer.type'
import { getAppMetadata } from '../plan/metadata/metadata'
import PoiService from '@/services/poi-browser.service'
import { getCustomPOIsFromDataRequest } from '../plan/filter/pois.helper'
import BrowserEnvironmentResolver from './environment.helper'
import { Plan } from '@workspaces/types'
import type { Environment, Metadata, Poi } from '@workspaces/types'
import { isNonEmptyArray } from '@/utils/arrayUtils'
import { buildPoiIconUrlsBySelection } from '@/helpers/poi-icons.helper'
import sharedPlanService from '@/services/sharedPlan.service'

export function managePoisLayerVisibility(
  legend: Plan.Legend,
  filter: PoisFilter,
  updateDeckInstance: boolean,
): void {
  // legend props
  const legendPoisVisibility = legend.poisVisibility ?? true
  const legendPoisBufferVisibility = legend.poisBufferVisibility ?? true

  const legendPoisIconsVisibility =
    legendPoisVisibility &&
    Boolean(legend.poisIconsVisibility) &&
    !isPoiLogosCacheEmpty()

  if (!legendPoisVisibility) {
    layerService.hideLayer(POI_LAYER, updateDeckInstance)
    layerService.hideLayer(CUSTOM_POI_LAYER, updateDeckInstance)
    layerService.hideLayer(ISOCHRONES_LAYER, updateDeckInstance)
    layerService.hideLayer(POI_LOGO_LAYER, updateDeckInstance)
    layerService.hideLayer(CUSTOM_POI_LOGO_LAYER, updateDeckInstance)
  } else {
    if (filter && isNonEmptyArray(filter.data)) {
      if (legendPoisIconsVisibility) {
        layerService.showLayer(POI_LOGO_LAYER, {}, updateDeckInstance)
        layerService.showLayer(CUSTOM_POI_LOGO_LAYER, {}, updateDeckInstance)
      }
      if (!layerService.isVisible(POI_LAYER)) {
        if (filter.method === 'RADIUS') {
          layerService.showLayer(
            POI_LAYER,
            {
              visibleBuffer: legendPoisBufferVisibility,
            },
            updateDeckInstance,
          )
          layerService.showLayer(
            CUSTOM_POI_LAYER,
            {
              visibleBuffer: legendPoisBufferVisibility,
            },
            updateDeckInstance,
          )

          layerService.hideLayer(ISOCHRONES_LAYER, updateDeckInstance)
        } else {
          layerService.showLayer(
            POI_LAYER,
            { visibleBuffer: false },
            updateDeckInstance,
          )
          layerService.showLayer(
            CUSTOM_POI_LAYER,
            { visibleBuffer: false },
            updateDeckInstance,
          )
          layerService.showLayer(ISOCHRONES_LAYER, updateDeckInstance)
        }
      }
    } else {
      layerService.hideLayer(POI_LAYER, updateDeckInstance)
      layerService.hideLayer(CUSTOM_POI_LAYER, updateDeckInstance)
      layerService.hideLayer(ISOCHRONES_LAYER, updateDeckInstance)
      layerService.hideLayer(POI_LOGO_LAYER, updateDeckInstance)
      layerService.hideLayer(CUSTOM_POI_LOGO_LAYER, updateDeckInstance)
    }
  }
}

/** Every unique POI selection across all subplan filters (icons are per selection). */
function getPoiSelections(plan: Plan.Plan): FilterByPoisParamsRequestData[] {
  const seen = new Set<string>()
  const selections: FilterByPoisParamsRequestData[] = []
  for (const subfilter of (plan.filters ?? []) as Array<{
    proximity?: { data?: FilterByPoisParamsRequestData[] }
  }>) {
    for (const item of subfilter?.proximity?.data ?? []) {
      if (!item || seen.has(item.name)) {
        continue
      }
      seen.add(item.name)
      selections.push(item)
    }
  }
  return selections
}

/**
 * Logo source (URL + marker fit) per POI selection.
 *
 * A shared plan does not carry the assignments in its payload: a cloud
 * function serves them, already resolved to URLs (the files live under a plan
 * id the viewer never sees) and only while the plan's legend has the icons
 * mode enabled, so a rejection means there is nothing to draw (sc-563940).
 * Editing a plan resolves the stored basenames against the plan itself.
 */
async function resolvePoiLogoSources(
  metadata: Metadata.AppMetadata,
  environment: Environment.EnvironmentResolver,
  plan: Plan.Plan,
  poisIcons: Poi.PoisIcons,
): Promise<Record<string, Poi.PoiIconUrlEntry>> {
  if (environment.isSharePlan().sharing) {
    try {
      return await sharedPlanService.getPoisIcons(metadata, environment)
    } catch (error) {
      console.warn('[poi-logos] Shared plan POI icons unavailable', error)
      return {}
    }
  }
  return buildPoiIconUrlsBySelection(
    environment,
    (plan.id as string | undefined) ?? null,
    poisIcons,
  )
}

/**
 * Refreshes the POI icon-mode layers (sc-563940): rebuilds the logos cache
 * from the plan's icon assignments and shows/hides the two top-precedence
 * layers, which draw ALL selected POIs in icon mode (logos where assigned,
 * legacy circles otherwise). The existing POI layers hide their circles in
 * icon mode; their buffers are never touched (D8/D9).
 *
 * `customPOIsFeatures` is null when the caller has no fetched features at
 * hand (an icon assignment changes which image a feature draws, not the
 * features themselves), and the custom logo layer keeps its current data.
 *
 * Nothing is fetched while the legend's icons toggle is off: building the
 * cache downloads every assigned icon, so it only happens once the legend
 * actually asks for logos. Turning the toggle on goes through here again.
 */
export async function updatePoiLogosLayers(
  plan: Plan.Plan,
  poisIcons: Poi.PoisIcons,
  poisColors: Record<string, unknown>,
  customPOIsFeatures: GeoJSON.Feature[] | null,
  updateDeckInstance = true,
): Promise<void> {
  const metadata = getAppMetadata()
  const environment = BrowserEnvironmentResolver.getInstance()
  const iconsEnabled = Boolean(plan.legend?.poisIconsVisibility)

  if (iconsEnabled) {
    await updatePoiLogosCache(
      metadata,
      environment,
      getPoiSelections(plan),
      await resolvePoiLogoSources(metadata, environment, plan, poisIcons),
    )
  } else {
    clearPoiLogosCache()
  }

  const iconsVisible =
    iconsEnabled &&
    Boolean(plan.legend?.poisVisibility ?? true) &&
    !isPoiLogosCacheEmpty()

  layerService.updateLayer(
    POI_LOGO_LAYER,
    {
      visible: iconsVisible,
      poisColors,
      updateTriggers: { getFilterValue: Date.now(), getIcon: Date.now() },
    },
    updateDeckInstance,
  )

  // Data is set regardless of visibility so a later legend toggle only has
  // to flip `visible`.
  layerService.updateLayer(
    CUSTOM_POI_LOGO_LAYER,
    {
      ...(customPOIsFeatures ? { data: [...customPOIsFeatures] } : {}),
      visible: iconsVisible,
      poisColors,
      updateTriggers: { getIcon: Date.now() },
    },
    updateDeckInstance,
  )

  // The existing layers hide their circles in icon mode (buffers untouched).
  layerService.updateLayer(
    POI_LAYER,
    { poisIconsVisibility: iconsVisible },
    updateDeckInstance,
  )
  layerService.updateLayer(
    CUSTOM_POI_LAYER,
    { poisIconsVisibility: iconsVisible },
    updateDeckInstance,
  )
}

export async function getCustomPOIsGroupsPoints(
  proximityParams: FilterByPoisParamsRequestData[],
): Promise<FetchLayerDataResult | null> {
  const customPOIsGroupsIds = getCustomPOIsFromDataRequest(proximityParams)
  if (customPOIsGroupsIds.length === 0) {
    return null
  }

  const metadata = getAppMetadata()
  const data = await PoiService.fetchLayer(
    metadata,
    BrowserEnvironmentResolver.getInstance(),
    customPOIsGroupsIds,
  )
  return data
}

export function planHasPOIsFilter(plan: Plan.Plan): boolean {
  const proximityData: unknown[] = plan.filters
    .reduce((acum: unknown[], subfilter: { proximity: { data: never[] } }) => {
      const proximityData = subfilter.proximity?.data || []
      return [...acum, ...proximityData]
    }, [])
    .flat()
  return proximityData.length > 0
}
